'use client'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import PasswordInput from '@/components/custom-ui/password-input'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { LoginSchema } from '@/schemas'
import { login, LoginRes } from './login-form.actions'
import { ReactNode, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Dictionary } from '@/localization/dictionary'
import DefaultLoadingSpinner from '@/components/custom-ui/loading-spinners/default-loading-spinner'
import { cn } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import Text from '@/components/custom-ui/text'
import { withCartStoreSuspense } from '@/stores/cart-store'

export type LoginFormVariant = 'default' | 'checkout'

const fieldClassNames: Record<
  LoginFormVariant,
  { label: string; emailInput: string; passwordInput: string }
> = {
  default: {
    label: 'text-xs font-normal text-foreground',
    emailInput:
      'h-auto rounded-none px-4 py-3.5 text-base aria-[invalid=true]:border-[#CC544E]',
    passwordInput: 'aria-[invalid=true]:border-[#CC544E]',
  },
  checkout: {
    label: 'text-base font-normal leading-5 text-foreground',
    emailInput:
      'h-12 rounded-none px-4 py-3 text-base aria-[invalid=true]:border-[#CC544E]',
    passwordInput: 'h-12 py-3 text-base aria-[invalid=true]:border-[#CC544E]',
  },
}

interface LoginFormProps {
  dict: Dictionary['loginForm']
  onForgotPassword: () => void
  onSuccess: () => void
  title: ReactNode
  register?: ReactNode
  variant?: LoginFormVariant
  refreshPageAfterLogin?: boolean
}

const LoginForm = ({
  dict,
  onForgotPassword,
  onSuccess,
  title,
  register,
  variant = 'default',
  refreshPageAfterLogin = true,
}: LoginFormProps) => {
  const classNames = fieldClassNames[variant]
  const [errorMessage, setErrorMessage] = useState<LoginRes | null>(null)
  const queryClient = useQueryClient()

  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const form = useForm<z.infer<typeof LoginSchema>>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof LoginSchema>) => {
    try {
      setIsLoading(true)
      const error = await login(values, { applyPreferredLanguage: true })
      if (error) {
        setErrorMessage(error)
      } else {
        queryClient.invalidateQueries()
        if (refreshPageAfterLogin) {
          router.refresh()
        }
        onSuccess()
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      {title}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex w-full flex-col gap-5"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem className="grid w-full items-center">
                <FormLabel className={classNames.label}>{dict.email}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={dict.email}
                    className={classNames.emailInput}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem className="grid w-full items-center">
                <FormLabel className={classNames.label}>
                  {dict.password}
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    autoComplete="password"
                    className={classNames.passwordInput}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            onClick={onForgotPassword}
            variant="plain"
            size="plain"
            className="w-fit"
            type="button"
          >
            <Text
              as="p"
              underline
              className="w-fit border-primary-600 text-primary-600"
            >
              {dict.forgotPassword}
            </Text>
          </Button>

          <div className="flex flex-col gap-1">
            <Button
              type="submit"
              className={cn(isLoading && 'pointer-events-none')}
            >
              {isLoading ? (
                <DefaultLoadingSpinner />
              ) : (
                <Text as="p" uppercase>
                  {dict.login}
                </Text>
              )}
            </Button>
            {errorMessage && (
              <Text
                as="p"
                size={14}
                lineHeight={20}
                className="text-destructive"
              >
                {dict[errorMessage]}
              </Text>
            )}
          </div>
        </form>
      </Form>
      {register}
    </>
  )
}

export default withCartStoreSuspense(LoginForm)
